import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { USER_OPERATIONS, RESOURCE_TYPE, MESSAGE_TYPE, JLR_PLANT } from "../../constants";
import defaultStyles from "./SubAssembly.module.css";
import "../../cssModules/switch.css";
import { usePlantAndLinesStore } from "../../providers/PlantAndLinesStore/PlantAndLinesStore";
import { withAllowedOperationsProvider } from "../../hocs";
import Table from "../Table/Table";
import SubAssemblyToolbar from "../SubAssemblyToolbar/SubAssemblyToolbar";
import { getSubAssemblyConfigurations, deleteSubAssembly } from "../../apis/calls";
import { usePopupManager } from "../../providers/PopupManager/PopupManager";
import AddSubAssemblyForm from "../AddSubAssemblyForm/AddSubAssemblyForm";

const SubAssembly = ({
  setHasActiveEdits,
  hasActiveEdits,
  handleCountChange,
}) => {
  const ROW_HEIGHT = 38;

  const RESOURCE_OPERATION = USER_OPERATIONS.GET_SUB_ASSEMBLY_CONFIGURATION;

  const { showError, showInternalError, showPopup } = usePopupManager();

  const [plant] = usePlantAndLinesStore().plantState;
  const [line] = usePlantAndLinesStore().lineState;

  const [styles] = useState(defaultStyles);

  const [rowsData, setRowsData] = useState({
    loading: false,
    data: [],
    filteredData: [],
  });

  const [recordCount, setRecordCount] = useState(0);
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);

  const setCount = (count) => {
    setRecordCount(count);
  };

  const fixedColumns = [
    {
      width: 120,
      title: "Plant Id",
      field: "plantId",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 140,
      title: "Line Id",
      field: "lineId",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 200,
      title: "Line Name",
      field: "lineName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 250,
      title: "Sub Assembly Name",
      field: "subAssemblyName",
      enableSearch: true,
      enableFilter: true,
    },
    {
      width: 150,
      title: "Ready For Drop",
      field: "readyForDropText",
      enableSearch: false,
      enableFilter: true,
    },
    {
      width: 150,
      title: "Drop Start Seq",
      field: "dropStartSeq",
      enableSearch: true,
      enableFilter: false,
    },
    {
      width: 150,
      title: "Drop End Seq",
      field: "dropEndSeq",
      enableSearch: true,
      enableFilter: false,
    },
  ];

  const getSubAssemblyData = async () => {
    if (plant.value && line.value) {
      setRowsData((prev) => ({
        ...prev,
        loading: true,
      }));

      try {
        const response = await getSubAssemblyConfigurations(plant.value, line.value);

        const updatedData = (response?.data || []).map((row) => ({
          ...row,
          readyForDropText: row.readyForDropFlag ? "Yes" : "No",
        }));

        setRowsData({
          loading: false,
          data: updatedData,
          filteredData: updatedData,
        });
      } catch (err) {
        showInternalError({ error: err });
        setRowsData({ loading: false, data: [], filteredData: [] });
      }
    } else {
      setRowsData({ loading: false, data: [], filteredData: [] });
    }
  };

  const handleDelete = async (selectedRows) => {
    if (!selectedRows || !selectedRows.length) {
      showError({
        contextText: "No Rows Selected",
        info: "Please select at least one Sub Assembly to delete.",
      });
      return;
    }

    try {
      const payload = selectedRows.map((row) => ({
        id: row.id,
      }));

      await deleteSubAssembly(payload);

      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: "Sub Assembly deleted successfully.",
        info: "Selected Sub Assemblies have been deleted.",
      });

      getSubAssemblyData();
    } catch (error) {
      showError({
        contextText: "Delete Failed",
        info: "Unable to delete Sub Assembly.",
      });
    }
  };

  const handleAddClick = () => {
    if (!plant.value || !line.value) {
      showError({
        contextText: "Selection Required",
        info: "Please select both a Plant and a Line before adding a Sub Assembly.",
      });
      return;
    }
    setIsAddFormOpen(true);
  };

  const handleAddFormClose = () => {
    setIsAddFormOpen(false);
  };

  const handleAddSuccess = () => {
    getSubAssemblyData();
  };

  useEffect(() => {
    handleCountChange(recordCount);
  }, [recordCount, handleCountChange]);

  useEffect(() => {
    setHasActiveEdits(rowsData.data.some((row) => row.edited));
  }, [rowsData.data, setHasActiveEdits]);

  useEffect(() => {
    if (plant.value && line.value) {
      getSubAssemblyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plant.value, line.value]);

  return (
    <>
      <SubAssemblyToolbar plantName={plant.label} />

      <div className={styles.container}>
        <div className={styles.tableView} style={{ height: "100%" }}>
          <Table
            columns={fixedColumns}
            defaultColumns={[...fixedColumns]}
            rowHeight={ROW_HEIGHT}
            rows={rowsData.filteredData}
            setHasActiveEdits={setHasActiveEdits}
            setAsnCount={setCount}
            isDataLoading={rowsData.loading}
            secondaryActions={[
              {
                name: "DELETE SUB ASSEMBLY",
                actionFn: handleDelete,
                authOperation: RESOURCE_OPERATION,
                backgroundColor: "#dc3545",
              },
              {
                name: "ADD SUB ASSEMBLY",
                actionFn: handleAddClick,
                authOperation: RESOURCE_OPERATION,
              },
            ]}
          />
        </div>
      </div>

      <AddSubAssemblyForm
        title="Add Sub Assembly Configuration"
        open={isAddFormOpen}
        handleClose={handleAddFormClose}
        plant={plant}
        line={line}
        lineBackgroundColor={plant.label === JLR_PLANT ? "green" : "blue"}
        cancelButtonText="CANCEL"
        submitButtonText="SUBMIT"
        onSuccess={handleAddSuccess}
      />
    </>
  );
};

SubAssembly.propTypes = {
  hasActiveEdits: PropTypes.bool.isRequired,
  setHasActiveEdits: PropTypes.func.isRequired,
  handleCountChange: PropTypes.func.isRequired,
};

export default withAllowedOperationsProvider(
  SubAssembly,
  false,
  RESOURCE_TYPE.SUB_ASSEMBLY_CONFIGURATION
);
